import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, Trash2, ShoppingBag, Loader2, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';

const CartDrawer = () => {
  const {
    cartItems,
    totalQuantity,
    subtotal,
    total,
    checkoutUrl,
    isCartOpen,
    setIsCartOpen,
    isLoading,
    updateQuantity,
    removeFromCart,
  } = useCart();

  const handleCheckout = () => {
    if (checkoutUrl) {
      // Close the cart drawer first
      setIsCartOpen(false);
      
      // Convert checkout URL to use Shopify's myshopify.com domain
      // This ensures checkout works without redirect issues
      const SHOPIFY_DOMAIN = 'zk8n0h-1h.myshopify.com';
      let shopifyCheckoutUrl = checkoutUrl;
      
      try {
        const urlObj = new URL(checkoutUrl);
        // Always use the myshopify.com domain for checkout
        urlObj.hostname = SHOPIFY_DOMAIN;
        urlObj.protocol = 'https:';
        shopifyCheckoutUrl = urlObj.toString();
      } catch (e) {
        console.error('Error parsing checkout URL:', e);
      }
      
      // Open Shopify checkout in a new tab
      // This bypasses any custom domain redirect issues
      window.open(shopifyCheckoutUrl, '_blank');
    }
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-brand-softBeige">
              <div className="flex items-center gap-3">
                <ShoppingBag className="w-6 h-6 text-brand-deepGreen" strokeWidth={1.5} />
                <h2 className="font-heading text-xl font-light text-brand-deepGreen">
                  Your Cart
                </h2>
                <span className="bg-brand-warmGold text-white text-xs px-2 py-1 rounded-full">
                  {totalQuantity}
                </span>
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:bg-brand-softBeige rounded-full transition-colors"
                aria-label="Close cart"
              >
                <X className="w-5 h-5 text-brand-coconutBrown" strokeWidth={1.5} />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                  <Loader2 className="w-8 h-8 text-brand-warmGold animate-spin" />
                </div>
              )}

              {cartItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <ShoppingBag className="w-16 h-16 text-brand-softBeige mb-4" strokeWidth={1} />
                  <p className="text-brand-coconutBrown/70 font-body mb-4">
                    Your cart is empty
                  </p>
                  <button
                    onClick={() => {
                      setIsCartOpen(false);
                      const shopSection = document.getElementById('shop');
                      if (shopSection) shopSection.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="btn-primary text-sm"
                  >
                    Browse Products
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex gap-4 pb-6 border-b border-brand-softBeige last:border-0"
                    >
                      {/* Product Image */}
                      <div className="w-20 h-24 bg-brand-softBeige rounded-lg overflow-hidden flex-shrink-0">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.imageAlt || item.productTitle}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ShoppingBag className="w-8 h-8 text-brand-coconutBrown/30" />
                          </div>
                        )}
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-base font-light text-brand-deepGreen truncate">
                          {item.productTitle}
                        </h3>
                        {item.variantTitle !== 'Default Title' && (
                          <p className="text-xs text-brand-coconutBrown/60 font-body mt-1">
                            {item.variantTitle}
                          </p>
                        )}
                        <p className="text-brand-warmGold font-heading text-lg mt-2">
                          ${item.price.toFixed(2)}
                        </p>

                        {/* Quantity Controls */}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                              disabled={item.quantity <= 1}
                              className="w-8 h-8 flex items-center justify-center rounded-full border border-brand-softBeige hover:bg-brand-softBeige disabled:opacity-50 transition-colors"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center font-body text-sm">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center rounded-full border border-brand-softBeige hover:bg-brand-softBeige transition-colors"
                              aria-label="Increase quantity"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="p-2 text-brand-coconutBrown/50 hover:text-red-500 transition-colors"
                            aria-label="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {cartItems.length > 0 && (
              <div className="border-t border-brand-softBeige p-6 space-y-4">
                {/* Subtotal */}
                <div className="flex justify-between items-center">
                  <span className="font-body text-brand-coconutBrown/70">Subtotal</span>
                  <span className="font-heading text-lg text-brand-deepGreen">${subtotal}</span>
                </div>

                {/* Total */}
                <div className="flex justify-between items-center pt-2 border-t border-brand-softBeige">
                  <span className="font-body font-medium text-brand-deepGreen">Total</span>
                  <span className="font-heading text-2xl text-brand-deepGreen">${total}</span>
                </div>

                {/* Checkout Button */}
                <button
                  onClick={handleCheckout}
                  disabled={!checkoutUrl || isLoading}
                  className="w-full bg-brand-deepGreen text-white py-4 px-6 rounded-full font-body font-semibold text-sm tracking-wider uppercase flex items-center justify-center gap-2 hover:bg-brand-deepGreen/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Proceed to Checkout
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="text-xs text-center text-brand-coconutBrown/50 font-body">
                  Secure checkout powered by Shopify
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
